<script lang="ts">
	import { _ } from 'svelte-i18n';
	import { ViewMode, type FilterOption } from '../../../types/ListTypes';
	import DocumentTag from '../../document/DocumentTag.svelte';
	import type { SmallDocumentRepresentation } from '../../../types/DocumentTypes';
	import { getColorForString } from '../../../helper/Colors';
	import { formatBigWithLocale, formatDate } from '../../../helper/Parser';
	import type { SettingDocumentType } from '../../../types/BasicTypes';
	import BaseListItemDocument from '../BaseListItemDocument.svelte';
	import BaseStatusInfo from '../BaseStatusInfo.svelte';
	import { documentDragStore } from '../../../stores/DocumentViewStore.svelte';
	import type { FolderDocumentListStore } from '../../../stores/FolderStore.svelte';

	interface Props {
		store: FolderDocumentListStore;
		style: string;
		item: SmallDocumentRepresentation;
		viewMode: ViewMode;
		selectedItemCount: number;
		filterOptions: FilterOption[];
		index: number;
		onClick: (e: MouseEvent) => void;
		onDblClick: (e: MouseEvent) => void;
		onDragStartRequest: (event: DragEvent) => void;
		onChildDeleted: () => void;
		onRequestContextmenu: () => void;
	}

	let {
		store,
		style,
		item,
		viewMode,
		selectedItemCount,
		filterOptions,
		index,
		onClick,
		onDblClick,
		onDragStartRequest,
		onChildDeleted,
		onRequestContextmenu
	}: Props = $props();

	function handleStartDrag(event: DragEvent) {
		//The draged item should as well be selected
		item.selected = true;

		//Then set the type on the drag info
		event.dataTransfer?.setData('document', 'true');

		onDragStartRequest(event);
	}

	function handleDragEnd() {
		//Release the data.
		$documentDragStore = null;
	}
</script>

<BaseListItemDocument
	{onClick}
	{onRequestContextmenu}
	{onChildDeleted}
	{item}
	{viewMode}
	{selectedItemCount}
	onCopyDocument={async (
		settingDocumentType: SettingDocumentType,
		overrideWithDefaultValues: boolean
	) => {
		await store.copySmallDocumentRepresentationStore(item.id, {
			targetDocumentType: settingDocumentType.id,
			overrideWithDocumentTypeValues: overrideWithDefaultValues
		});
	}}
	onDeleteDocument={async (targetId: number) => {
		await store.deleteDocumentStore(targetId);
	}}
	onMoveDocument={async (folderId: number, documentInformationId: number) => {
		//Update the document in the store
		await store.patchDocumentInformationStore({ fkFolder: folderId }, documentInformationId);
	}}
>
	<button
		{style}
		draggable={true}
		onclick={onClick}
		ondblclick={onDblClick}
		ondragstart={handleStartDrag}
		ondragend={handleDragEnd}
		class:selected={item.selected}
		class:doc-container-card={viewMode != ViewMode.LIST}
		class:documentDragStore={viewMode == ViewMode.LIST}
		class="w-full text-left border-2 border-transparent justify-self-center relative
		 outline-secondary-200
		 hover:border-secondary-200 dark:hover:border-secondary-500
		 selected:border-secondary-200 dark:selected:border-secondary-500
		  {index % 2 == 0 ? '' : 'bg-background dark:bg-dark-200'}"
		class:col-span-2={viewMode == ViewMode.BIG}
	>
		{#if viewMode == ViewMode.LIST}
			{#each filterOptions as filter, headerIndex}
				{#if filter.tableOption?.showAsTableHeader != null ? filter.tableOption?.showAsTableHeader : true}
					{#if filter.key === 'type'}
						<!--Type-->
						<div class="content-center flex items-center gap-2 p-2 border-2 border-transparent">
							<div
								style="background-color: {getColorForString(item.type)};"
								class="w-4 h-4 rounded"
							></div>
							<p class="truncate">
								{item.type}
							</p>
						</div>
					{:else if filter.key == 'shortName'}
						<!--short name-->
						<p class="truncate p-2 border-2 border-transparent">
							{item.shortName}
						</p>
					{:else if filter.key == 'name'}
						<!--Name-->
						<p title={item.name} class="truncate p-2 border-2 border-transparent">
							{item.name}
						</p>
					{:else if filter.key == 'documentDate'}
						<!--Document date-->
						<p class="p-2 border-2 border-transparent">
							{formatDate(item.documentDate)}
						</p>
					{:else if filter.key == 'lastModified'}
						<!--last modified-->
						<p class="p-2 border-2 border-transparent">
							{formatDate(item.lastModified)}
						</p>
					{:else if filter.key == 'total'}
						<!--total-->
						<p class="p-2 border-2 border-transparent">
							{formatBigWithLocale(item.total)}
						</p>
					{:else if filter.key == 'tags'}
						<!--Tag container-->
						<div
							class="flex gap-2 gap-y-1 items-center flex-wrap h-full p-2 border-2 border-transparent"
						>
							{#each item.documentInformation.tags.sortTags() as tag}
								<DocumentTag value={tag} />
							{/each}
						</div>
					{:else if filter.key == 'status'}
						<!--Status containter-->
						<div>
							<BaseStatusInfo {item} {viewMode}></BaseStatusInfo>
						</div>
					{/if}
				{/if}
			{/each}
		{:else}
			<div class="h-full flex flex-col">
				<!--Type-->
				<div class="grid grid-cols-[auto_1fr] items-center gap-2">
					<div
						style="background-color: {getColorForString(item.type)};"
						class="w-4 h-4 rounded"
					></div>
					<p class="truncate font-bold">
						{item.type}
					</p>
				</div>

				<!--short name-->
				<p class="truncate">
					{item.shortName}
				</p>

				<!--Name-->
				<p title={item.name} class="truncate font-bold mt-2">
					{item.name}
				</p>

				<!--Dates, total-->
				<div class="mt-4">
					<div class="flex justify-between">
						<p class="font-bold">
							{formatBigWithLocale(item.total)}
						</p>
						<p></p>
					</div>

					<div class="flex justify-between">
						<p>
							{$_('documentview.lbl_created') + ':'}
						</p>
						<p>
							{formatDate(item.documentDate)}
						</p>
					</div>

					<div class="flex justify-between">
						<p>
							{$_('documentview.lbl_edited') + ':'}
						</p>
						<p>
							{formatDate(item.lastModified)}
						</p>
					</div>
				</div>

				{#if viewMode == ViewMode.BIG}
					<!--QR-->
					<div class="mt-4">
						<p>
							{$_('documentview.basic_data_edit.lbl_document_qr_reference') + ':'}
						</p>
					</div>
					<p class="text-sm">
						{item.qrReferenceNr || '-'}
					</p>

					<!--Memo-->
					<div class="mt-4">
						<p>
							{$_('documentview.basic_data_edit.lbl_document_memo') + ':'}
						</p>
					</div>
					<p class="grow">{item.documentInformation.memo || ''}</p>
				{/if}

				<!--Tag container-->
				<div class="flex gap-x-2 gap-y-1 items-center flex-wrap mt-2 mr-4">
					{#each item.documentInformation.tags as tag}
						<DocumentTag value={tag} />
					{/each}
				</div>
			</div>
		{/if}
	</button>
</BaseListItemDocument>

<style>
	.doc-container-card :global {
    border-radius: 0.375rem;
    --tw-bg-opacity: 1;
    background-color: rgb(253 253 253 / var(--tw-bg-opacity, 1));
    padding: 1.5rem
}
.doc-container-card :global:where(.dark, .dark *) {
    --tw-bg-opacity: 1;
    background-color: rgb(125 125 125 / var(--tw-bg-opacity, 1))
}
</style>
